import { Injectable } from '@angular/core';
import {
  AgentsService,
  ConfigurationsService,
  DistributorsService,
  GroupsService,
  ListHelperService,
  LocalesService,
  ProfileService,
  StoreService,
  UiService
} from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { Router } from '@angular/router';
import { EntityQuestion } from 'core/components/forms/question-entity';
import { lastValueFrom } from 'rxjs';

@Injectable()
export class GroupsListService {
  userRole: any;

  agentQuestion: EntityQuestion;
  partnerQuestion: EntityQuestion;
  searchAgentsComponent: any;
  searchPartnersComponent: any;

  constructor(
    public agentsService: AgentsService,
    public distributorsService: DistributorsService,
    public groupsService: GroupsService,
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService,
    public uiService: UiService,
    public router: Router,
    private configurationsService: ConfigurationsService,
    private localesService: LocalesService
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
    this.initFilters();
  }

  initFilters() {
    this.agentQuestion = new EntityQuestion({
      key: 'agentIds',
      entityOptions: {
        nameField: 'email',
        listParser: (agent) => this.listHelper.parseAgent(agent)
      },
      dataProvider: this.agentsService
    });
    this.partnerQuestion = new EntityQuestion({
      key: 'distributorId',
      dataProvider: this.distributorsService
    });

    this.agentQuestion.getOptions$().subscribe((options) => this.setSearchAgentsOptions(options));
    this.partnerQuestion.getOptions$().subscribe((options) => this.setSearchPartnersOptions(options));
  }

  async getDatasource(listId, options: any = {}) {
    if (options.filters) {
      if (options.filters.agentIds) {
        this.agentQuestion.preload(options.filters.agentIds);
      }
      if (options.filters.distributorId) {
        this.partnerQuestion.preload(options.filters.distributorId);
      }
    }
    const dataSource = new TableDatasource({
      sort: '-startDate',
      aggregates: ['licensesResume', 'state'],
      populate: ['agents', 'partner'],
      columns: await this.getColumns(),
      load: (props) => this.fetch(props, dataSource),
      storeState: {
        enabled: true,
        key: `${listId}_${this.userRole.name}`
      },
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters, aggregates, populate }, dataSource: TableDatasource) {
    const { clientId, agentIds, distributorId, state, ...$filterValues } = filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource);
    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      aggregates,
      populate,
      clientId,
      agentIds,
      distributorId,
      state,
      $filter,
      // Get groups' courseTitle translated to user's language
      $lang: this.profileService.getLang()
    };
    return this.groupsService.fetch(paginationOptions).then((response) => response);
  }

  async getColumns() {
    const columns = [
      {
        source: 'name',
        label: this.translate.instant('nouns.name'),
        sortable: true,
        style: { width: '160px' },
        cellRenderer: (renderer, value, group) =>
          renderer`<div class="ellipsis">${this.listHelper.getRouterLinkRenderer(
            renderer,
            `/groups/${group.id}`,
            value
          )}</div>`
      },
      {
        source: 'distributorId',
        label: this.translate.instant('nouns.distributor'),
        visible: false,
        style: { width: '160px' },
        showInColumnChooser: this.profileService.canExecute('groups.tableColumns.partner'),
        valueParser: (value, group) => (value ? group.partner.name : ''),
        cellRenderer: (renderer, value, group) =>
          renderer`<div class="ellipsis">${this.listHelper.getRouterLinkRenderer(
            renderer,
            `/distributors/${group.distributorId}`,
            value
          )}</div>`,

        searchRenderer: (renderer, value, callback) => {
          return renderer`
            <gl-input-tag
              .optionLabel="${this.translate.instant('actions.select')}"
              .options="${this.partnerQuestion.options}"
              .createTags="${false}"
              .value="${value}"
              tagsLimit="1"
              small
              @onKeyup="${(ev) => this.onSearchPartners(ev)}"
              @onChange="${(ev) => callback(ev.detail.value)}"
            >
            </gl-input-tag>
          `;
        }
      },
      {
        source: 'clientName',
        label: this.translate.instant('groups.header.clientName'),
        sortable: true,
        style: { width: '160px' },
        visible: this.profileService.canExecute('groups.tableColumns.clientName'),
        showInColumnChooser: this.profileService.canExecute('groups.tableColumns.clientName'),
        cellRenderer: (renderer, value, group) =>
          renderer`<div class="ellipsis">${this.listHelper.getRouterLinkRenderer(
            renderer,
            `/clients/${group.clientId}`,
            value
          )}</div>`
      },
      {
        source: 'courseTitle',
        label: this.translate.instant('groups.header.gameItineraryName'),
        style: { width: '160px' },
        sortable: true
      },
      {
        source: 'licensesResume.total',
        label: this.translate.instant('students.label'),
        dataType: DataTypes.number,
        allowFiltering: false,
        sortable: true,
        style: { width: '100px' }
      },
      {
        source: 'startDate',
        label: this.translate.instant('nouns.startDate'),
        dataType: DataTypes.datetime,
        sortable: true,
        style: { width: '110px' },
        valueParser: (value, group) => this.listHelper.parseDate(value, group.timezone)
      },
      {
        source: 'endDate',
        label: this.translate.instant('nouns.endDate'),
        dataType: DataTypes.datetime,
        sortable: true,
        style: { width: '110px' },
        valueParser: (value, group) => this.listHelper.parseDate(value, group.timezone)
      },
      {
        source: 'state',
        label: this.translate.instant('nouns.status'),
        style: { width: '130px' },
        valueParser: (value) => this.translate.instant(`properties.${value}`),
        searchRenderer: (renderer, value, callback) => {
          const options = [
            { key: 'any', value: this.translate.instant('properties.any') },
            { key: 'active', value: this.translate.instant('properties.active') },
            { key: 'finished', value: this.translate.instant('properties.finished') },
            { key: 'future', value: this.translate.instant('properties.future') }
          ];
          return renderer`<gl-select small .options="${options}" .value="${value || 'any'}" @onChange="${(e) =>
            callback(e.detail.value !== 'any' ? e.detail.value : '')}" ></gl-select>`;
        }
      },
      {
        source: 'licensesResume',
        label: this.translate.instant('nouns.activityReport'),
        allowFiltering: false,
        tooltip: await this.getInfoText('groups.tableColumns.licensesResume').then((message) => message),
        style: { width: '100px' },
        valueParser: (value) => {
          if (!value.total) {
            return '';
          }
          const data = [
            { label: this.translate.instant('licenses.state.created'), value: value.created },
            { label: this.translate.instant('licenses.state.playing'), value: value.beforeDemo + value.afterDemo },
            { label: this.translate.instant('licenses.state.completed'), value: value.completed }
          ];
          return data;
        },
        cellRenderer: (renderer, value) => {
          if (!value) {
            return '';
          }
          return renderer`<gl-progress-tooltip .data="${value}"></gl-progress-tooltip>`;
        }
      },
      {
        source: 'hasBonus',
        dataType: DataTypes.boolean,
        label: this.translate.instant('groups.bonus.verb'),
        style: { width: '200px' },
        cellRenderer: (renderer, hasBonus, group) => {
          let message = '';
          if (hasBonus) {
            if (group.bonusData && group.bonusData.tutor) {
              message = group.bonusData.caf ? 'groups.bonus.noun' : 'groups.bonus.cafPending';
            } else {
              message = 'groups.bonus.bonusDataPending';
            }
          }

          return renderer`<div class="ellipsis" title="${message ? this.translate.instant(message) : ''}">${
            message ? this.translate.instant(message) : ''
          }</div>`;
        }
      },
      {
        source: 'agentIds',
        label: this.translate.instant('agents.label'),
        sortable: false,
        style: { width: '200px' },
        visible: false,
        showInColumnChooser: this.profileService.canExecute('groups.tableColumns.agents'),
        valueParser: (value, group) => {
          const { agents = [] } = group;
          if (agents && agents.length) {
            return agents.map((agent) => this.listHelper.parseAgent(agent));
          }
          return '';
        },
        cellRenderer: (renderer, value) => {
          const colorTrigger = ['#e1e4e8'];
          const colorDefault = ['#c3ccdc'];
          if (value && value.length) {
            const [firstAgent, ...restAgents] = value;
            return renderer`
              <div class="tags">
                ${renderer`<gl-tag .colors="${colorDefault}" label="${firstAgent}"></gl-tag>`}
                ${
                  restAgents.length === 0
                    ? ''
                    : renderer`
                      <gl-tooltip posLeft light>
                        <gl-tag slot="trigger" .colors="${colorTrigger}" label="+${restAgents.length}"></gl-tag>
                        ${restAgents.map((agent) => renderer`<gl-tag label="${agent}"></gl-tag>`)}
                      </gl-tooltip>
                    `
                }
                </div>
                `;
          }
          return '';
        },
        searchRenderer: (renderer, value, callback) => {
          return renderer`
            <gl-input-tag
              .value="${value}"
              .options="${this.agentQuestion.options}"
              tagsLimit="1"
              small
              .createTags="${false}"
              .optionLabel="${this.translate.instant('actions.select')}"
              @onKeyup="${(ev) => this.onSearchAgents(ev)}"
              @onChange="${(ev) => callback(ev.detail.value)}"
            >
            </gl-input-tag>
          `;
        }
      },
      {
        source: 'tags',
        label: this.translate.instant('nouns.tags'),
        visible: this.userRole.name === 'clientAdmin',
        style: { width: '200px' },
        dataType: DataTypes.array
      }
    ];
    return columns;
  }

  onSearchAgents(ev) {
    this.searchAgentsComponent = ev.target;
    this.agentQuestion.onKeyup(ev.detail.value);
  }

  setSearchAgentsOptions(options) {
    if (this.searchAgentsComponent) {
      this.searchAgentsComponent.options = options;
    }
  }

  onSearchPartners(ev) {
    this.searchPartnersComponent = ev.target;
    this.partnerQuestion.onKeyup(ev.detail.value);
  }

  setSearchPartnersOptions(options) {
    if (this.searchPartnersComponent) {
      this.searchPartnersComponent.options = options;
    }
  }

  /**
   * Get the text from contentful/configurations
   * @param key
   * @private
   */
  private async getInfoText(key: string): Promise<any> {
    return lastValueFrom(
      this.configurationsService.getInfoMessage$(
        'toolTip',
        'fields.keys',
        key,
        'textoDeAyuda',
        this.localesService.getBrowserLocale(),
        true
      )
    );
  }
}
